import {
  useFocusedIds,
  useIsFocusActive,
} from '@components/MainStage/focusOn.store';
import useFilteredCanvas from '@components/MainStage/hooks/useFilteredCanvas';
import { setHoverState } from '@components/MainStage/hover.store';
import {
  getListSearchState,
  setListSearchState,
} from '@components/MainStage/listSearch.store';
import {
  getMultiSelectionState,
  setMultiSelectionState,
} from '@components/MainStage/multiSelection.store';
import useStore from '@components/MainStage/store';
import { useProject } from '@context/Project/ProjectProvider';
import CanvasModelRenderer from '@utils/canvas/CanvasModelRenderer';
import {
  getVisibleNodeIds,
  hideNodesOutsideFocus,
  unselectEdges,
  unselectNodes,
} from '@views/ReportsModel/exportScope';

const usePrepareModelToExport = () => {
  const {
    toolbox: { mode },
  } = useProject();

  const { setNodes, setEdges, setPhantoms, getNodes, edges, phantoms } =
    useStore();
  const { nodes } = useFilteredCanvas();
  const isFocusActive = useIsFocusActive();
  const focusedIds = useFocusedIds();

  const prepareModelToExport = () => {
    const exportNodes = unselectNodes(
      hideNodesOutsideFocus(nodes, isFocusActive, focusedIds),
    );
    const visibleNodeIds = getVisibleNodeIds(exportNodes);

    const { selectedIds, anchorId } = getMultiSelectionState();
    const { query } = getListSearchState();

    setMultiSelectionState({ selectedIds: [], anchorId: null });
    setListSearchState({ query: '' });
    setHoverState({ hoveredId: null, pinnedId: null });

    const initialData = {
      canvasTransform: '',
      nodes: JSON.parse(JSON.stringify(getNodes())),
      edges: JSON.parse(JSON.stringify(edges)),
      phantoms: JSON.parse(JSON.stringify(phantoms)),
    };

    const viewport = document.querySelector<HTMLDivElement>(
      '.react-flow__viewport.react-flow__container',
    );

    if (viewport) {
      // save current canvas transform
      initialData.canvasTransform = viewport.style.transform;

      // set canvas transform to 0,0, and scale 100%
      viewport.style.transform = 'translate(250px, 0px) scale(1)';
    }

    const isEdgeVisible = ({
      source,
      target,
    }: {
      source: string;
      target: string;
    }) => visibleNodeIds.has(source) && visibleNodeIds.has(target);

    setEdges(unselectEdges(edges.filter(isEdgeVisible)));
    setPhantoms(unselectEdges(phantoms.filter(isEdgeVisible)));

    // remove empty rows
    CanvasModelRenderer.removeEmptyRows(mode!, exportNodes);

    // undo all changes
    return () => {
      if (viewport && initialData.canvasTransform) {
        viewport.style.transform = initialData.canvasTransform;
      }

      setNodes(initialData.nodes);
      setEdges(initialData.edges);
      setPhantoms(initialData.phantoms);

      setMultiSelectionState({ selectedIds, anchorId });
      setListSearchState({ query });

      setTimeout(() => {
        CanvasModelRenderer.redrawFullCanvasLayers(mode!);
      }, 10);
    };
  };

  return { prepareModelToExport };
};

export default usePrepareModelToExport;
